<script>
  import { Chart, Badge } from 'flowbite-svelte';
  import { ChartLineUpSolid, CloseOutline } from 'flowbite-svelte-icons';
  import { formatNumberWithIntl } from '../utils';
  import { Heading, Truncate, PercentChange, ClickIcon } from '@button/ui';
  import CopyButton from './CopyButton.svelte';

  export let currentDailyResults = [];
  export let previousDailyResults = [];
  export let topCuratedShortlinksData = [];

  let showChartModal = false;
  let activeChartIndex = null;

  function showChart(index) {
    activeChartIndex = index;
    showChartModal = true;
  }

  function closeChart() {
    showChartModal = false;
    activeChartIndex = null;
  }
</script>

{#if currentDailyResults?.length === 0}
  <div class="flex h-full flex-col items-center justify-center space-y-6">
    <img src="./images/no-data.svg" alt="No Data" />
    <Heading>No data</Heading>
  </div>
{/if}
<div class="mx-auto max-w-4xl">
  <div class="pb-4 pt-6">
    {#each currentDailyResults as stats, index}
      <div class="grid grid-cols-12 items-center gap-6 border-b py-6 pt-10">
        <div class="col-span-6 flex min-w-0">
          <div class="mr-4 flex">
            <Heading level={2}>
              {index + 1}.
            </Heading>
          </div>
          <div class="relative -top-4 min-w-0 flex-1">
            <span>
              <Heading level={2} class="pb-2">
                <Truncate
                  text={topCuratedShortlinksData[index].curatedByPage.name}
                  length={20}
                />
              </Heading>
            </span>
            <div
              class="flex min-w-0 flex-shrink-0 items-center text-xs text-gray-500"
            >
              <a
                href={topCuratedShortlinksData[index].curatedByPage.shortlink}
                target="_blank"
                class="mr-1"
              >
                <Truncate
                  text={topCuratedShortlinksData[index].curatedByPage.shortlink}
                  length={30}
                />
              </a>
              <div class="flex-shrink-0">
                <CopyButton
                  shortlink={topCuratedShortlinksData[index].curatedByPage
                    .shortlink}
                />
              </div>
            </div>
          </div>
        </div>

        <div
          class="col-span-3 flex min-w-0 flex-col justify-center break-words"
        >
          <div class="mb-2 flex flex-col">
            <div class="flex items-center justify-center">
              <span class="flex-shrink-0">
                <ClickIcon color="black" />
              </span>
              <span class="ml-1 flex-shrink-0 text-2xl font-bold text-black">
                {formatNumberWithIntl(
                  topCuratedShortlinksData[index].curatedByPage
                    .total_mobile_journeys,
                )}
              </span>
            </div>
            <span class="flex-shrink-0 text-center text-sm text-black">
              total clicks
            </span>
          </div>

          <div class="flex w-full text-center">
            <Badge
              rounded
              color={previousDailyResults[index]?.total_shortlink_clicks >
              stats?.total_shortlink_clicks
                ? 'red'
                : 'green'}
              class="inline-flex items-center rounded-full px-[0.3rem] py-[0.1rem]"
            >
              <div
                class="flex origin-center scale-[0.7] transform items-center text-[0.6rem] sm:text-xs"
              >
                <PercentChange
                  previous={previousDailyResults[index]?.total_shortlink_clicks}
                  current={stats.total_shortlink_clicks}
                  className="text-xs font-medium whitespace-nowrap"
                />
                <span class="ml-1 whitespace-nowrap text-xs">
                  vs. previous period
                </span>
              </div>
            </Badge>
          </div>
        </div>

        <div class="col-span-3 flex h-full items-center justify-center">
          <button
            class="p-2 text-gray-500 hover:text-gray-700 focus:outline-none"
            on:click={() => showChart(index)}
          >
            <ChartLineUpSolid />
          </button>
        </div>
      </div>
    {/each}

    {#if showChartModal && activeChartIndex !== null}
      <div
        class="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
      >
        <div
          class="max-h-[90vh] w-full max-w-2xl overflow-auto rounded-lg bg-white p-6"
        >
          <div class="mb-4 flex items-center justify-between">
            <h3 class="text-xl font-bold">Shortlink Clicks Chart</h3>
            <button
              on:click={closeChart}
              class="text-gray-500 hover:text-gray-700"
            >
              <CloseOutline class="h-3 w-3" />
            </button>
          </div>

          <Chart
            options={{
              chart: {
                height: '300px',
                maxWidth: '100%',
                fontFamily: 'Inter, sans-serif',
                toolbar: {
                  show: false,
                },
              },
              stroke: {
                width: 2,
                curve: 'smooth',
              },
              series: [
                {
                  name: 'Total Shortlink Clicks',
                  data: currentDailyResults[activeChartIndex].daily_results.map(
                    (result) => result.shortlink_clicks,
                  ),
                  color:
                    previousDailyResults[activeChartIndex]
                      ?.total_shortlink_clicks >
                    currentDailyResults[activeChartIndex]
                      ?.total_shortlink_clicks
                      ? '#E02424'
                      : '#0E9F6E',
                },
              ],
              xaxis: {
                categories: currentDailyResults[
                  activeChartIndex
                ].daily_results.map((result) => result.date),
                labels: {
                  show: true,
                  style: {
                    fontFamily: 'Inter, sans-serif',
                    cssClass: 'text-xs font-normal fill-gray-500',
                  },
                },
                axisBorder: {
                  show: true,
                },
                axisTicks: {
                  show: true,
                },
              },
              yaxis: {
                show: true,
              },
              grid: {
                show: true,
              },
              tooltip: {
                enabled: true,
              },
            }}
          />
        </div>
      </div>
    {/if}
  </div>
</div>
